import { useRef, useState } from 'react'

import { SPACE_SEARCH_SERVICE } from '../../Constants'
import { PartialBrief } from '../../Types/Brief'

import { Space } from './useSpaceRecommendations'

export default function useSpaceRecommendationSearch(
  brief?: PartialBrief,
  limitToLocation = false
): {
  loading: boolean
  spaces: Space[]
  search: (query: string, cleanPrompt?: string) => void
} {
  const [loading, setLoading] = useState(false)
  const [spaces, setSpaces] = useState<Space[]>([])
  const abortRef = useRef<AbortController | null>(null)

  const buildLocationFilter = () => {
    if (limitToLocation && brief?.lat != null && brief?.lon != null) {
      return {
        filters: {
          location: {
            lat: brief.lat,
            lon: brief.lon,
            radius:
              brief.radius != null
                ? brief.radius.distance * (brief.radius.unit === 'mi' ? 1609 : 1000)
                : undefined,
          },
        },
      }
    }
    if (brief?.leadFeedCity) {
      return { filters: { location: { city: brief.leadFeedCity } } }
    }
    return {}
  }

  const search = async (query: string, cleanPrompt?: string) => {
    if (!query.trim()) return

    if (abortRef.current) {
      abortRef.current.abort()
    }

    const controller = new AbortController()
    abortRef.current = controller

    setLoading(true)
    setSpaces([])

    try {
      const response = await fetch(`${SPACE_SEARCH_SERVICE}`, {
        signal: controller.signal,
        method: 'POST',
        body: JSON.stringify({
          query,
          ...(cleanPrompt && { cleanPrompt }),
          matchThreshold: 0.7,
          matchCount: 100,
          searchMode: 'venue-name',
          ...buildLocationFilter(),
        }),
        headers: { 'Content-Type': 'application/json' },
      })

      const { spaces: results } = await response.json()
      setSpaces(results as Space[])
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setSpaces([])
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false)
      }
    }
  }

  return { loading, spaces, search }
}
